import { AddCircle, Favorite, Send, MusicNote } from '@mui/icons-material';
import { Avatar, Box, Card, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';

const SnapchatPostCard = ({ post, profile }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Media Handling
  let mediaUrl = '';
  let thumbUrl = '';
  let isVideo = false;

  if (post.media_urls?.length > 0) {
    const first = post.media_urls[0];
    mediaUrl = first.media_url || first.url || '';
    thumbUrl = first.thumb_url || first.thumbnail_url || mediaUrl;
    isVideo = first.media_type === 'VIDEO';
  } else if (post.media_url || post.image_url) {
    mediaUrl = post.media_url || post.image_url;
    thumbUrl = post.thumbnail_url || mediaUrl;
    isVideo = post.media_type === 'VIDEO';
  }

  const title = post.title || '';
  const description = post.description || post.caption || '';
  const createdTime = post.created_at || post.created_time;

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play().catch(() => {});
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" p={1}>
      <Card
        sx={{
          width: isMobile ? 300 : 340,
          height: 560,           // Reduced height
          borderRadius: '18px',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#0a0a0a',
          color: 'white',
          boxShadow: '0 8px 25px rgba(0,0,0,0.6)',
        }}
      >
        {/* Media Area - Larger */}
        <Box sx={{ position: 'relative', width: '100%', height: '490px', bgcolor: '#000' }}>
          {isVideo && mediaUrl ? (
            <video
              ref={videoRef}
              src={mediaUrl}
              poster={thumbUrl}
              loop
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onClick={togglePlay}
            />
          ) : (
            mediaUrl && (
              <img
                src={mediaUrl}
                alt="Snapchat Story"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )
          )}
        </Box>

        {/* Top Bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <Avatar
            src={profile?.profile_image}
            sx={{ width: 36, height: 36, border: '2px solid white', mr: 1.5 }}
          />
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {profile?.profile_name || 'Snapchat'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {CommonHelper.GetDateFormat(createdTime)}
            </Typography>
          </Box>
        </Box>

        {/* Caption */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 98,
            left: 16,
            right: 80,
            color: 'white',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          }}
        >
          {title && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              {title}
            </Typography>
          )}

          {description && (
            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: showFullDescription ? 'initial' : 3,
                overflow: 'hidden',
                fontSize: '0.9rem',
                lineHeight: 1.4,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Bottom Bar - Original Sound */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 30,
            left: 16,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'white',
          }}
        >
          <MusicNote sx={{ fontSize: 18 }} />
          <Typography variant="caption" sx={{ fontSize: 12.5, opacity: 0.9 }}>
            Original Sound • {profile?.profile_name}
          </Typography>
        </Box>

        {/* Right Side Actions */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 18,
            right: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <IconButton sx={{ color: 'white', p: 0.5 }}>
            <Favorite sx={{ fontSize: 30 }} />
          </IconButton>

          <IconButton sx={{ color: 'white', p: 0.5 }}>
            <Send sx={{ fontSize: 26, transform: 'rotate(-45deg)' }} />
          </IconButton>

          {/* Profile with Add Button */}
          <Box sx={{ position: 'relative', mt: 1 }}>
            <Avatar
              src={profile?.profile_image}
              sx={{
                width: 42,
                height: 42,
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -15,
                right: -4,
                bgcolor: '#0a0a0a',
                borderRadius: '50%',
                p: '2px',
              }}
            >
              <AddCircle sx={{ color: '#00ff9f', fontSize: 22 }} />
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default SnapchatPostCard;